import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, MessageSquareWarning, Hammer, FileText, Building2,
  AlertTriangle, BadgeIndianRupee, UserSquare2, Menu, X, LogOut, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const CRM_LINKS = [
  { to: "/crm/dashboard",    label: "Dashboard", Icon: LayoutDashboard, tid: "nav-crm-dashboard" },
  { to: "/crm/customers",    label: "Customers", Icon: Users,           tid: "nav-crm-customers" },
  { to: "/crm/tickets",      label: "Tickets",   Icon: MessageSquareWarning, tid: "nav-crm-tickets" },
  { to: "/crm/construction", label: "Construction", Icon: Hammer,       tid: "nav-crm-construction" },
  { to: "/crm/documents",    label: "Documents", Icon: FileText,        tid: "nav-crm-documents" },
];

const MGMT_LINKS = [
  { to: "/management/dashboard",  label: "Command Centre", Icon: LayoutDashboard, tid: "nav-mgmt-dashboard" },
  { to: "/management/projects",   label: "Projects",       Icon: Building2,       tid: "nav-mgmt-projects" },
  { to: "/management/customers",  label: "Customers",      Icon: Users,           tid: "nav-mgmt-customers" },
  { to: "/management/employees",  label: "Employees",      Icon: UserSquare2,     tid: "nav-mgmt-employees" },
  { to: "/management/escalations",label: "Escalations",    Icon: AlertTriangle,   tid: "nav-mgmt-escalations" },
  { to: "/management/collections",label: "Collections",    Icon: BadgeIndianRupee,tid: "nav-mgmt-collections" },
];

const Sidebar = ({ portal, user, collapsed, setCollapsed, onLogout, onNavigate, isMgmtUser }) => {
  const links = portal === "management" ? MGMT_LINKS : CRM_LINKS;
  return (
    <aside className={`rc-sidebar ${collapsed ? "!w-[64px]" : ""}`} style={{ width: collapsed ? 64 : 240 }} data-testid="sidebar">
      <div className={`flex items-center gap-2 mb-6 ${collapsed ? "justify-center" : "px-2"}`}>
        <img src="/radiance-logo.jpeg" alt="Radiance" className="w-9 h-9 rounded-lg object-cover" />
        {!collapsed && (
          <div className="leading-tight">
            <div className="rc-headline">Radiance</div>
            <div className="rc-caption" style={{ color: "var(--rc-blue)" }}>Connect</div>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="rc-caption uppercase tracking-wider px-3 mb-1">{portal === "management" ? "Management" : "CRM"}</div>
      )}

      <nav className="flex flex-col gap-1">
        {links.map(({ to, label, Icon, tid }) => (
          <NavLink key={to} to={to} data-testid={tid}
            className={({ isActive }) => `rc-sb-item ${isActive ? "active" : ""}`}
            onClick={onNavigate} title={collapsed ? label : ""}>
            <Icon size={18} strokeWidth={1.75} />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {isMgmtUser && portal === "management" && (
          <NavLink to="/crm/dashboard" className="rc-sb-item mt-4" onClick={onNavigate} title="CRM Portal">
            <Hammer size={18} strokeWidth={1.75} /> {!collapsed && <span>CRM Portal</span>}
          </NavLink>
        )}
        {isMgmtUser && portal === "crm" && (
          <NavLink to="/management/dashboard" className="rc-sb-item mt-4" onClick={onNavigate} title="Management">
            <LayoutDashboard size={18} strokeWidth={1.75} /> {!collapsed && <span>Management</span>}
          </NavLink>
        )}
      </nav>

      <div className="mt-auto pt-3 border-t border-[var(--rc-line)]">
        {!collapsed && (
          <div className="px-3 py-2">
            <div className="rc-headline truncate">{user?.employee_name}</div>
            <div className="rc-footnote capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
        )}
        <div className="flex items-center gap-2 px-2 pt-1">
          <button className="rc-btn rc-btn-sm rc-btn-secondary" onClick={() => setCollapsed((v) => !v)} title="Collapse" data-testid="sidebar-collapse">
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            {!collapsed && <span>Collapse</span>}
          </button>
          <button className="rc-btn rc-btn-sm rc-btn-ghost" onClick={onLogout} title="Logout" data-testid="logout-btn">
            <LogOut size={16} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default function StaffLayout({ portal }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isMgmtUser = user?.role === "crm_manager" || user?.role === "management";

  return (
    <div className="min-h-screen rc-section flex">
      <header className="md:hidden rc-frosted fixed top-0 left-0 right-0 z-40 border-b border-[var(--rc-line)] flex items-center justify-between px-4 py-3">
        <button onClick={() => setOpen(true)} data-testid="open-drawer"><Menu size={22} /></button>
        <div className="flex items-center gap-2">
          <img src="/radiance-logo.jpeg" alt="Radiance" className="w-7 h-7 rounded-md" />
          <span className="rc-headline">{portal === "management" ? "Management" : "CRM"}</span>
        </div>
        <button onClick={logout} data-testid="logout-btn-mobile"><LogOut size={20} /></button>
      </header>

      <div className="hidden md:block">
        <Sidebar portal={portal} user={user} collapsed={collapsed} setCollapsed={setCollapsed} onLogout={logout} isMgmtUser={isMgmtUser} />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute left-0 top-0 bottom-0 w-[80%] max-w-[300px] bg-[var(--rc-near-white)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end p-2">
              <button onClick={() => setOpen(false)} data-testid="close-drawer"><X size={20} /></button>
            </div>
            <Sidebar portal={portal} user={user} collapsed={false} setCollapsed={setCollapsed} onLogout={logout} isMgmtUser={isMgmtUser} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-[60px] md:pt-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 py-5 md:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
